import type { GetProjectsResult } from '@efficiently/client-domain-projects';

import { PROJECT_NAVIGATOR } from '../constants/project-navigator.constant';
import type {
	ProjectNavigatorDomain,
	ProjectNavigatorGroup,
	ProjectNavigatorProject,
} from '../models/project-navigator-domain.model';

/**
 * One project exactly as the `GetProjects` query returns it.
 *
 * Derived from the generated query type rather than hand-written, so a field the selection set stops
 * asking for becomes a compile error here instead of an `undefined` at runtime. Every field on it is
 * nullable except `name`, which is why so much of this file is about absent values.
 */
export type ProjectNavigatorSourceRow = GetProjectsResult['projects'][number];

/** A row already known to carry an `_id`, so the mapping below never has to answer "and if it has none". */
type IdentifiedProjectRow = ProjectNavigatorSourceRow & { readonly _id: string };

/**
 * Heading for a domain whose organization came back without a name.
 *
 * A domain still has to render — dropping every project of an organization because its account name
 * was null would hide real projects — so it renders under a heading that reads as an absence rather
 * than as an empty line the user cannot click or explain.
 */
const UNNAMED_DOMAIN_LABEL = 'Unnamed organization';

/**
 * Domain id for rows carrying no `orgId`. They collapse into one bucket rather than becoming one
 * single-project domain each, which is what an index-derived key would have produced.
 */
const UNASSIGNED_DOMAIN_ID = 'unassigned-domain';

/**
 * Appended to a domain's id to make its group's id. A group id has to be unique across the whole
 * tree, not only inside its domain, because the markup keys its `@for` rows off it.
 */
const PROJECTS_GROUP_ID_SUFFIX = ':projects';

/** Sort position of a row with no `orgProjectNumber`: after every numbered row, never interleaved. */
const UNNUMBERED_SORT_KEY = Number.MAX_SAFE_INTEGER;

/** Keeps the rows the tree can actually address, and tells TypeScript their `_id` is a string. */
function hasId(row: ProjectNavigatorSourceRow): row is IdentifiedProjectRow {
	return row._id != null;
}

/**
 * Numeric-aware order over `orgProjectNumber`: 2 before 10, because the number is a number here and
 * never a string. Rows without one sort last, and ties fall back to the name so the same input always
 * produces the same tree regardless of the order the server happened to return.
 */
function compareRows(a: ProjectNavigatorSourceRow, b: ProjectNavigatorSourceRow): number {
	const byNumber = (a.orgProjectNumber ?? UNNUMBERED_SORT_KEY) - (b.orgProjectNumber ?? UNNUMBERED_SORT_KEY);

	if (byNumber !== 0) {
		return byNumber;
	}

	return a.name.localeCompare(b.name);
}

/**
 * `${orgProjectNumber}${PROJECT_LABEL_SEPARATOR}${name}` — both what the row renders and what the search
 * filter matches, so the number is searchable precisely because it is part of the label. The separator
 * is the shared constant rather than an inline string for that reason: the filter matches this exact
 * format. A row with no number renders its name alone rather than a label starting `null - `.
 */
function projectLabel(row: ProjectNavigatorSourceRow): string {
	if (row.orgProjectNumber == null) {
		return row.name;
	}

	return `${row.orgProjectNumber}${PROJECT_NAVIGATOR.PROJECT_LABEL_SEPARATOR}${row.name}`;
}

/**
 * The name of the organization a bucket belongs to, taken from the first row that actually carries one.
 *
 * Reading only the first row would leave a whole domain unnamed whenever that one row came back without
 * its `orgEntity`, even though every other row of the same organization has the name.
 */
function domainLabel(rows: readonly ProjectNavigatorSourceRow[]): string {
	for (const row of rows) {
		const name = row.orgEntity?.account?.name;

		if (name != null && name.length > 0) {
			return name;
		}
	}

	return UNNAMED_DOMAIN_LABEL;
}

/** One row as the menu renders it. `viewOnly` matches the existing `ProjectUi` mapping exactly. */
function toNavigatorProject(
	row: IdentifiedProjectRow,
	currentOrgId: string | null | undefined,
): ProjectNavigatorProject {
	return {
		id: row._id,
		label: projectLabel(row),
		isSample: row.isSample ?? false,
		viewOnly: row.isEditAvailable === false,
		isOwnedByCurrentOrg: currentOrgId != null && row.orgId === currentOrgId,
	};
}

/** One domain and its single group, or `null` when no row in the bucket could be addressed. */
function toDomain(
	domainId: string,
	rows: readonly ProjectNavigatorSourceRow[],
	currentOrgId: string | null | undefined,
): ProjectNavigatorDomain | null {
	const projects = [...rows]
		.sort(compareRows)
		.filter(hasId)
		.map(row => toNavigatorProject(row, currentOrgId));

	if (projects.length === 0) {
		return null;
	}

	const group: ProjectNavigatorGroup = {
		id: `${domainId}${PROJECTS_GROUP_ID_SUFFIX}`,
		label: PROJECT_NAVIGATOR.PROJECTS_GROUP_LABEL,
		projects,
	};

	const label = domainId === UNASSIGNED_DOMAIN_ID ? UNNAMED_DOMAIN_LABEL : domainLabel(rows);

	return {
		id: domainId,
		label,
		groups: [group],
	};
}

/** The rows bucketed by `orgId`, insertion-ordered, each bucket a fresh array the caller may sort. */
function groupRowsByDomain(
	rows: readonly ProjectNavigatorSourceRow[],
): ReadonlyMap<string, readonly ProjectNavigatorSourceRow[]> {
	const byDomain = new Map<string, ProjectNavigatorSourceRow[]>();

	for (const row of rows) {
		const domainId = row.orgId ?? UNASSIGNED_DOMAIN_ID;
		const bucket = byDomain.get(domainId);

		if (bucket === undefined) {
			byDomain.set(domainId, [row]);
			continue;
		}

		bucket.push(row);
	}

	return byDomain;
}

/**
 * The Domains & Projects tree, built from the project rows the store loaded.
 *
 * The rules, and each one's reason:
 *
 * - **One domain per distinct `orgId`**, labelled with that organization's `orgEntity.account.name`,
 *   domains ordered by that label and then by id, so two organizations sharing a name still land in a
 *   fixed order instead of whichever one the server listed first. Rows with no `orgId` share a single
 *   bucket: they are a data defect, and one domain per unassigned row would bury every real
 *   organization under them.
 * - **Exactly one group per domain**, labelled `PROJECT_NAVIGATOR.PROJECTS_GROUP_LABEL`. The seam types
 *   already allow several because the design has room for them; today there is one.
 * - **Projects sort numeric-aware on `orgProjectNumber`** — 2 before 10. The number is a number in the
 *   generated type, so this is an arithmetic comparison and cannot degrade into a string sort.
 * - **A row with no `_id` is dropped.** It is unaddressable: nothing can navigate to it, and two such
 *   rows would collide on the `@for` track key the markup half derives from the id, which is a runtime
 *   error rather than a cosmetic one. A domain left with no addressable row is dropped in turn rather
 *   than rendered as an empty heading.
 * - **A missing `orgEntity`, `account` or name never throws.** The domain keeps its projects and renders
 *   under `UNNAMED_DOMAIN_LABEL`; losing a heading must not lose the menu. The unassigned bucket always
 *   renders under that same heading rather than reading a name off its rows, because a row can lack an
 *   `orgId` while still carrying an `orgEntity`, and it must not appear as a second domain named after
 *   a real organization.
 * - **The input is never mutated.** Every array here is one this function created; the store's cached
 *   row list is only read.
 *
 * Pure by contract: no Angular, no injection, no logging, no side effect. Nothing it returns is derived
 * from anything but its two arguments, so the store can call it inside a `computed()`.
 *
 * @param projects The rows as the `GetProjects` query returned them.
 * @param currentOrgId The organization the session belongs to. Legitimately absent for some sessions,
 *   and then no project is owned by it.
 * @returns One domain per organization, ordered by label and then by id, so the same rows always
 *   produce the same order. Empty when nothing addressable was loaded.
 */
export function buildProjectNavigatorTree(
	projects: readonly ProjectNavigatorSourceRow[],
	currentOrgId: string | null | undefined,
): readonly ProjectNavigatorDomain[] {
	const domains: ProjectNavigatorDomain[] = [];

	for (const [domainId, rows] of groupRowsByDomain(projects)) {
		const domain = toDomain(domainId, rows, currentOrgId);

		if (domain !== null) {
			domains.push(domain);
		}
	}

	return domains.sort((a, b) => a.label.localeCompare(b.label) || a.id.localeCompare(b.id));
}
